import type { Grade } from '@viastud/utils'
import dayjs from 'dayjs'

import type ProfessorAvailability from '#models/professor_availability'
import type Slot from '#models/slot'

export const DEFAULT_SLOT_CAPACITY = 3
export const MIN_TRIAL_COMMENT_LENGTH = 10
const ONE_HOUR_IN_MINUTES = 60
const MINUTES_PER_DAY = 24 * ONE_HOUR_IN_MINUTES

type StudentState = Grade | 'SIGNUP_BONUS'
export const durationsByStudentState: Record<number, StudentState[]> = {
  30: ['SIGNUP_BONUS'],
  60: ['SIXIEME', 'CINQUIEME', 'QUATRIEME', 'TROISIEME', 'SECONDE'],
  90: ['PREMIERE', 'TERMINALE'],
}

export const allowedWindows: Record<number, { startHour: number; endHourInclusive: number }> = {
  0: { startHour: 16, endHourInclusive: 21 }, // Lundi
  1: { startHour: 16, endHourInclusive: 21 }, // Mardi
  2: { startHour: 13, endHourInclusive: 21 }, // Mercredi
  3: { startHour: 16, endHourInclusive: 21 }, // Jeudi
  4: { startHour: 16, endHourInclusive: 21 }, // Vendredi
  5: { startHour: 10, endHourInclusive: 21 }, // Samedi
  6: { startHour: 10, endHourInclusive: 21 }, // Dimanche
}

export function overlaps(aStart: number, aEnd: number, bStart: number, bEnd: number): boolean {
  return aStart < bEnd && bStart < aEnd
}

function toMinutes(dayOfWeek: number, hour: number) {
  return dayOfWeek * MINUTES_PER_DAY + hour * ONE_HOUR_IN_MINUTES
}

export function isDurationValid(duration: number, studentState: StudentState) {
  const window = durationsByStudentState[duration]

  if (!window) return false
  return window.includes(studentState)
}

export function isTeacherCommentRequired(duration: number) {
  return isDurationValid(duration, 'SIGNUP_BONUS')
}

export function isTeacherCommentValid(comment: string | undefined) {
  return (comment?.trim().length ?? 0) >= MIN_TRIAL_COMMENT_LENGTH
}

export function getDurationByStudentState(studentState: StudentState) {
  const duration = Object.keys(durationsByStudentState).find((key) =>
    durationsByStudentState[Number(key)].includes(studentState)
  )

  return duration ? Number(duration) : 30
}

export function isSlotAllowedForStudents(
  dayIndex: number,
  startHour: number,
  duration: number
): boolean {
  const window = allowedWindows[dayIndex]
  if (!window) return false

  const windowStartHourInMinute = window.startHour * ONE_HOUR_IN_MINUTES
  const windowEndHourInMinute = window.endHourInclusive * ONE_HOUR_IN_MINUTES

  const startHourInMinute = startHour * ONE_HOUR_IN_MINUTES
  const endHourInMinute = startHourInMinute + duration

  const isSlotWithinWindow = !(
    (startHourInMinute < windowStartHourInMinute || endHourInMinute > windowEndHourInMinute + 30) // +30 for 1h30
  )
  return isSlotWithinWindow
}

export function isSlotMatchingDayCriteria(
  slot: Slot,
  weekStart: string,
  dayOfWeek: number,
  hour: number
) {
  return (
    dayjs(slot.weekStart.toJSDate()).tz('Europe/Paris').format('YYYY-MM-DD') === weekStart &&
    slot.dayOfWeek === dayOfWeek &&
    slot.hour === hour
  )
}

export function isAvailabilityFree(
  professorAvailability: ProfessorAvailability,
  duration: number,
  slots: Slot[]
) {
  let isFree = true

  if (slots.length === 0) return isFree

  const startAvailability = toMinutes(professorAvailability.dayOfWeek, professorAvailability.hour)
  const endAvailability = startAvailability + duration

  for (const slot of slots) {
    if (slot.dayOfWeek !== professorAvailability.dayOfWeek) continue

    const startSlot = toMinutes(slot.dayOfWeek, slot.hour)
    const endSlot = startSlot + slot.duration

    const isOverlap = overlaps(startAvailability, endAvailability, startSlot, endSlot)

    if (isOverlap) {
      isFree = false
      break
    }
  }
  return isFree
}
